'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { APPLY_TYPES, OIL_SALE_TYPES, PRICE_APP_METHODS } from '@/components/features/approval/approval-common';
import { Checkbox } from '@/components/ui/checkbox';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

/**
 * GET master/cust-cost 응답 행(GeoDgResponse). src/lib/api 의 생성 타입은 V69 이전 모양이라 여기 둔다 -
 * 전체 재생성은 무관한 화면 타입까지 바꿔 별도로 진행한다.
 */
interface GeoDg {
  compCd?: string;
  storeCd?: string;
  storeName?: string;
  geoCode?: string;
  geoName?: string;
  oilCode?: string;
  oilName?: string;
  oilSaleType?: string;
  applyDt?: string;
  oilGubun?: string;
  priceAppMethod?: string;
  oilCost?: number;
  sourceRequestId?: number;
  /** CURRENT 적용중 / SCHEDULED 적용예정 / EXPIRED 새 계약에 밀려난 행 */
  status?: 'CURRENT' | 'SCHEDULED' | 'EXPIRED';
}

const STATUS_LABEL: Record<NonNullable<GeoDg['status']>, string> = {
  CURRENT: '적용중',
  SCHEDULED: '적용예정',
  EXPIRED: '지난계약',
};

function codeName(codes: { code: string; name: string }[], code: string | undefined): string {
  if (!code) return '';
  return codes.find(c => c.code === code)?.name ?? code;
}

/** 그리드는 코드값 대신 이름을 보여준다 - 코드표는 결재 화면(B220)과 같은 것을 쓴다 */
function toRow(row: GeoDg) {
  return {
    ...row,
    statusName: row.status ? STATUS_LABEL[row.status] : '',
    storeLabel: row.storeName || row.storeCd || '',
    oilSaleTypeName: codeName(OIL_SALE_TYPES, row.oilSaleType),
    oilGubunName: codeName(APPLY_TYPES, row.oilGubun),
    priceAppMethodName: codeName(PRICE_APP_METHODS, row.priceAppMethod),
  };
}

export function CustCostManagement() {
  const { toast } = useToast();
  const [geoDgData, setGeoDgData] = useState<GeoDg[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // 같은 거래처·유종·판매구분은 적용일마다 행이 쌓인다 - 기본은 지금 유효하거나 앞으로 적용될 행만 본다
  const [hideExpired, setHideExpired] = useState(true);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores } = useOrganization();

  const fetchGeoDgData = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/cust-cost', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as { result?: GeoDg[] };
      setGeoDgData(data.result || []);
    } catch {
      toast({
        title: '조회 실패',
        description: '고객단가 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchGeoDgData();
  }, []);

  const rows = useMemo(
    () => geoDgData.filter(row => !hideExpired || row.status !== 'EXPIRED').map(toRow),
    [geoDgData, hideExpired]
  );

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('cust-cost')} />
      }
      title='고객단가 목록'
      fill
      pageHeader={
        <PageHeader
          onSearch={fetchGeoDgData}
          isLoading={isLoading}
          actions={
            <label className='flex items-center gap-2 text-sm'>
              <Checkbox checked={hideExpired} onCheckedChange={checked => setHideExpired(checked === true)} />
              지난 계약 숨기기
            </label>
          }
        />
      }
    >
      {/* 고객단가 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              edit: { readOnly: true, editable: false },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={rows}
          >
            <RGDataField fieldName={'statusName'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'storeLabel'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'geoCode'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'geoName'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'oilCode'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'oilName'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'oilSaleTypeName'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'applyDt'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'oilGubunName'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'priceAppMethodName'} dataType={RealGrid.ValueType.TEXT} />
            <RGDataField fieldName={'oilCost'} dataType={RealGrid.ValueType.NUMBER} />
            <RGDataField fieldName={'sourceRequestId'} dataType={RealGrid.ValueType.NUMBER} />
            <RGDataColumn fieldName={'statusName'} header={{ text: '상태' }} width={70} />
            <RGDataColumn fieldName={'storeLabel'} header={{ text: '매장' }} width={120} />
            <RGDataColumn fieldName={'geoCode'} header={{ text: '코드' }} width={50} />
            <RGDataColumn fieldName={'geoName'} header={{ text: '거래처명' }} width={130} />
            <RGDataColumn fieldName={'oilCode'} header={{ text: '유종코드' }} width={60} />
            <RGDataColumn fieldName={'oilName'} header={{ text: '유종명' }} width={70} />
            <RGDataColumn fieldName={'oilSaleTypeName'} header={{ text: '판매구분' }} width={90} />
            <RGDataColumn fieldName={'applyDt'} header={{ text: '적용일자' }} width={90} />
            <RGDataColumn fieldName={'oilGubunName'} header={{ text: '적용구분' }} width={100} />
            <RGDataColumn fieldName={'priceAppMethodName'} header={{ text: '단가적용방법' }} width={90} />
            <RGDataColumn
              fieldName={'oilCost'}
              header={{ text: '단가' }}
              width={80}
              numberFormat={'#,##0.##'}
              styleName={'text-right'}
            />
            <RGDataColumn fieldName={'sourceRequestId'} header={{ text: '결재요청' }} width={70} numberFormat={'0'} />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
